import { hexToBytes, splitResponses } from './dtc.ts';

// The parts of OBD-II that a code reader ignores and a scan tool does not:
// readiness monitors, PID support discovery, on-board test measurements, and
// the calibration identity of the ECU itself.

// ---------------------------------------------------------------------------
// Readiness monitors — mode 01 PID 01, bytes B, C and D
// ---------------------------------------------------------------------------
//
// Byte A is the lamp and the code count, which is all most tools read. The
// remaining three bytes say which self-tests the vehicle runs and which have
// finished since the last clear. That is what an emissions station checks, and
// it is the usual reason a car with no codes still fails inspection.
//
// Two bit layouts share the same bytes. Byte B bit 3 selects between them:
// clear for spark ignition, set for compression ignition. Reading a diesel
// with the petrol table produces plausible, wrong monitor names.

export type IgnitionType = 'spark' | 'compression';

export interface Monitor {
  name: string;
  supported: boolean;
  /** True when the test has run to completion since codes were last cleared. */
  complete: boolean;
  continuous: boolean;
}

export interface Readiness {
  milOn: boolean;
  dtcCount: number;
  ignition: IgnitionType;
  monitors: Monitor[];
  /** Emissions-test shorthand: every supported monitor has finished. */
  ready: boolean;
  incompleteCount: number;
}

const CONTINUOUS = ['Misfire', 'Fuel system', 'Comprehensive components'];

const SPARK_MONITORS = [
  'Catalyst',
  'Heated catalyst',
  'Evaporative system',
  'Secondary air system',
  'A/C refrigerant',
  'Oxygen sensor',
  'Oxygen sensor heater',
  'EGR system',
];

const COMPRESSION_MONITORS = [
  'NMHC catalyst',
  'NOx / SCR aftertreatment',
  'Reserved',
  'Boost pressure',
  'Reserved',
  'Exhaust gas sensor',
  'PM filter',
  'EGR / VVT system',
];

export function parseReadiness(raw: string): Readiness | null {
  // Each module reports its own monitors. A monitor is supported if any module
  // runs it, and complete only when every module running it has finished.
  let found = false;
  let milOn = false;
  let dtcCount = 0;
  let ignition: IgnitionType = 'spark';
  const supported = new Map<string, boolean>();
  const complete = new Map<string, boolean>();

  for (const response of splitResponses(raw)) {
    const at = response.indexOf('4101');
    if (at < 0) continue;
    const b = hexToBytes(response.slice(at + 4));
    if (b.length < 4) continue;

    found = true;
    milOn = milOn || (b[0] & 0x80) !== 0;
    dtcCount += b[0] & 0x7f;
    if ((b[1] & 0x08) !== 0) ignition = 'compression';

    const note = (name: string, isSupported: boolean, isComplete: boolean) => {
      if (name === 'Reserved') return;
      supported.set(name, (supported.get(name) ?? false) || isSupported);
      if (isSupported) complete.set(name, (complete.get(name) ?? true) && isComplete);
    };

    // Byte B: low nibble is support, bits 4-6 are "incomplete" flags.
    CONTINUOUS.forEach((name, i) => note(name, (b[1] & (1 << i)) !== 0, (b[1] & (1 << (i + 4))) === 0));

    // Bytes C and D: support and readiness for the non-continuous monitors.
    // A set bit in D means NOT complete, which is the opposite of intuition.
    const names = ignition === 'compression' ? COMPRESSION_MONITORS : SPARK_MONITORS;
    names.forEach((name, i) => note(name, (b[2] & (1 << i)) !== 0, (b[3] & (1 << i)) === 0));
  }

  if (!found) return null;

  const monitors: Monitor[] = [...supported.entries()].map(([name, isSupported]) => ({
    name,
    supported: isSupported,
    complete: isSupported ? (complete.get(name) ?? false) : true,
    continuous: CONTINUOUS.includes(name),
  }));

  const incompleteCount = monitors.filter((m) => m.supported && !m.complete).length;
  return { milOn, dtcCount, ignition, monitors, ready: incompleteCount === 0, incompleteCount };
}

// ---------------------------------------------------------------------------
// PID support discovery — 0100, 0120, 0140, 0160, 0180
// ---------------------------------------------------------------------------
//
// Each of these returns 32 bits covering the next 32 PIDs, with the last bit
// meaning "ask the next block too". Polling without consulting this wastes a
// round trip per unsupported PID, and on a serialised link that is the whole
// budget for live data.

export interface PidSupport {
  /** Every PID the vehicle answers, as 0x01-style numbers. */
  supported: Set<number>;
  /** Which blocks actually replied, for reporting. */
  blocks: number[];
}

export function parsePidSupport(raw: string, blockBase: number): { pids: number[]; hasNextBlock: boolean } {
  return parseSupportBitmask(raw, 0x41, blockBase);
}

/** Mode 06 advertises its monitor ids with the same bitmask shape. */
export function parseMonitorSupport(raw: string, blockBase: number): { pids: number[]; hasNextBlock: boolean } {
  return parseSupportBitmask(raw, 0x46, blockBase);
}

function parseSupportBitmask(
  raw: string,
  responseByte: number,
  blockBase: number,
): { pids: number[]; hasNextBlock: boolean } {
  const echo =
    responseByte.toString(16).toUpperCase().padStart(2, '0') +
    blockBase.toString(16).toUpperCase().padStart(2, '0');
  const pids: number[] = [];
  let hasNextBlock = false;

  for (const response of splitResponses(raw)) {
    const at = response.indexOf(echo);
    if (at < 0) continue;
    const bytes = hexToBytes(response.slice(at + 4));
    if (bytes.length < 4) continue;

    for (let byteIndex = 0; byteIndex < 4; byteIndex++) {
      for (let bit = 0; bit < 8; bit++) {
        // Bit 7 of the first byte is the lowest PID in the block.
        if ((bytes[byteIndex] & (0x80 >> bit)) === 0) continue;
        const pid = blockBase + byteIndex * 8 + bit + 1;
        if (pid % 0x20 === 0) hasNextBlock = true;
        else pids.push(pid);
      }
    }
  }
  return { pids: [...new Set(pids)].sort((a, b) => a - b), hasNextBlock };
}

// ---------------------------------------------------------------------------
// Mode 06 — on-board monitoring test results
// ---------------------------------------------------------------------------
//
// Where mode 03 says "the catalyst failed", mode 06 says "the catalyst
// measured 0.42 against a limit of 0.60", which is how a marginal system gets
// caught before it sets a code.
//
// Each record is monitor id, test id, a unit-and-scaling id, then the measured
// value and its limits as 16-bit numbers. The scaling table in J1979 is long
// and we implement only the part we are sure of. That costs nothing important:
// pass or fail is a comparison between the value and its own limits, so it is
// correct whether or not the numbers are converted to engineering units.

export interface MonitorTest {
  monitorId: number;
  testId: number;
  unitId: number;
  value: number;
  min: number | null;
  max: number | null;
  unit: string | null;
  /** False when the unit-and-scaling id is outside our table. */
  scaled: boolean;
  /** null when the record carries no usable limits, or the record is unusable. */
  pass: boolean | null;
  /**
   * Why this record cannot be judged, when it cannot.
   *
   * A limit pair where the floor exceeds the ceiling is impossible, so the
   * framing is wrong rather than the vehicle being broken. Saying so beats
   * reporting a confident failure built on a misread byte.
   */
  invalidReason: string | null;
  /** How much room is left against the tighter limit, as a fraction. */
  margin: number | null;
}

interface Scaling {
  factor: number;
  unit: string;
  signed?: boolean;
}

// Only entries we are confident about. Anything absent is reported raw.
const UNIT_SCALING: Record<number, Scaling> = {
  0x01: { factor: 1, unit: '' },
  0x02: { factor: 0.1, unit: '' },
  0x03: { factor: 0.01, unit: '' },
  0x04: { factor: 0.001, unit: '' },
  0x05: { factor: 0.0000305, unit: 'V' },
  0x06: { factor: 0.000305, unit: 'V' },
  0x07: { factor: 0.25, unit: 'rpm' },
  0x09: { factor: 1, unit: 'km/h' },
  0x0a: { factor: 0.122, unit: 'mV' },
  0x0b: { factor: 0.001, unit: 'V' },
  0x0c: { factor: 0.01, unit: 'V' },
  0x10: { factor: 1, unit: 'ms' },
  0x18: { factor: 0.001, unit: 's' },
  0x1a: { factor: 1, unit: 'count' },
  0x81: { factor: 1, unit: '', signed: true },
  0x84: { factor: 0.001, unit: '', signed: true },
};

const signed16 = (v: number) => (v & 0x8000 ? v - 0x10000 : v);

/**
 * Mode 06 on a pre-CAN vehicle.
 *
 * ISO 9141 and KWP use a different record entirely: `46 TID CID VALUE MAX/MIN`,
 * six bytes rather than nine, with no unit-and-scaling id. The high bit of the
 * component id chooses which side the single limit applies to - set means the
 * value must be at or above it, clear means at or below. Feeding these records
 * to the CAN walk finds nothing at all, which is how a 2005 Lexus reported no
 * on-board tests while happily returning a dozen.
 */
function parseLegacyMonitorTests(raw: string): MonitorTest[] {
  const tests: MonitorTest[] = [];

  for (const response of splitResponses(raw)) {
    const at = response.indexOf('46');
    if (at < 0) continue;
    const bytes = hexToBytes(response.slice(at + 2));

    for (let i = 0; i + 5 < bytes.length; i += 6) {
      const [testId, cid] = [bytes[i], bytes[i + 1]];
      if (testId === 0 && cid === 0) continue;

      const value = (bytes[i + 2] << 8) | bytes[i + 3];
      const limit = (bytes[i + 4] << 8) | bytes[i + 5];
      const limitIsMinimum = (cid & 0x80) !== 0;

      const min = limitIsMinimum ? limit : null;
      const max = limitIsMinimum ? null : limit;
      const pass = limitIsMinimum ? value >= limit : value <= limit;

      tests.push({
        monitorId: cid & 0x7f,
        testId,
        unitId: 0,
        value,
        min,
        max,
        unit: null,
        // Legacy records carry no scaling id, so the numbers are raw counts.
        scaled: false,
        pass,
        invalidReason: null,
        margin: null,
      });
    }
  }
  return tests;
}

export function parseMonitorTests(raw: string, legacy = false): MonitorTest[] {
  if (legacy) return parseLegacyMonitorTests(raw);
  const tests: MonitorTest[] = [];

  for (const response of splitResponses(raw)) {
    const at = response.indexOf('46');
    if (at < 0) continue;
    const bytes = hexToBytes(response.slice(at + 2));

    // Records are 9 bytes: MID TID UAS VAL(2) MIN(2) MAX(2).
    //
    // Some manufacturers, Ford among them, repeat the 0x46 service byte in
    // front of every record rather than only at the head of the response. A
    // fixed stride then slips by one byte per record and every field lands
    // wrong, which is how a healthy truck reported dozens of failed tests.
    // Re-syncing on that repeated header keeps the walk aligned.
    for (let i = 0; i + 8 < bytes.length; ) {
      if (bytes[i] === 0x46 && i + 9 < bytes.length) i += 1;
      if (i + 8 >= bytes.length) break;

      const at = i;
      const [monitorId, testId, unitId] = [bytes[at], bytes[at + 1], bytes[at + 2]];
      i += 9;
      if (monitorId === 0 && testId === 0) continue;

      const scaling = UNIT_SCALING[unitId];
      const conv = (hi: number, lo: number) => {
        const rawValue = (hi << 8) | lo;
        if (!scaling) return rawValue;
        const base = scaling.signed ? signed16(rawValue) : rawValue;
        return Number((base * scaling.factor).toPrecision(6));
      };

      const value = conv(bytes[at + 3], bytes[at + 4]);
      const min = conv(bytes[at + 5], bytes[at + 6]);
      const max = conv(bytes[at + 7], bytes[at + 8]);

      // A limit pair of 0 and 0xFFFF is the "no limit specified" convention.
      const hasMin = !(bytes[at + 5] === 0 && bytes[at + 6] === 0);
      const hasMax = !(bytes[at + 7] === 0xff && bytes[at + 8] === 0xff);

      const lo = hasMin ? min : null;
      const hi = hasMax ? max : null;

      let invalidReason: string | null = null;
      if (lo !== null && hi !== null && lo > hi) {
        invalidReason = 'Lower limit exceeds upper limit, so this record did not decode cleanly.';
      }

      const pass =
        invalidReason !== null || (lo === null && hi === null)
          ? null
          : (lo === null || value >= lo) && (hi === null || value <= hi);

      // Distance to the nearest limit, relative to the span between them.
      let margin: number | null = null;
      if (invalidReason === null && lo !== null && hi !== null && hi > lo) {
        margin = Math.min(value - lo, hi - value) / (hi - lo);
      }

      tests.push({
        monitorId,
        testId,
        unitId,
        value,
        min: lo,
        max: hi,
        unit: scaling?.unit || null,
        scaled: Boolean(scaling),
        pass,
        invalidReason,
        margin,
      });
    }
  }
  return tests;
}

/**
 * Fill in fields a stored record predates.
 *
 * Scans saved before `invalidReason` and `margin` existed come back without
 * them. Both are derivable from the value and its limits, so recompute rather
 * than render an old record with holes in it - an absent `margin` sailed
 * through a `!== null` guard and printed NaN, and an absent `invalidReason`
 * let a misframed record present itself as a confident failure.
 */
export function normalizeMonitorTest<T extends Partial<MonitorTest>>(test: T): T & MonitorTest {
  // The cast at the end is safe because every MonitorTest field is either
  // present on `test` or supplied below; the generic only carries extras
  // (monitorLabel) through untouched.
  const value = typeof test.value === 'number' ? test.value : 0;
  const min = typeof test.min === 'number' ? test.min : null;
  const max = typeof test.max === 'number' ? test.max : null;

  const invalidReason =
    test.invalidReason ??
    (min !== null && max !== null && min > max
      ? 'Lower limit exceeds upper limit, so this record did not decode cleanly.'
      : null);

  const pass =
    invalidReason !== null || (min === null && max === null)
      ? null
      : (min === null || value >= min) && (max === null || value <= max);

  let margin: number | null = null;
  if (invalidReason === null && min !== null && max !== null && max > min) {
    margin = Math.min(value - min, max - value) / (max - min);
  }

  return { ...(test as MonitorTest), value, min, max, invalidReason, pass, margin } as T & MonitorTest;
}

/** The standardised monitor ids. Manufacturer ranges are left as numbers. */
export function monitorName(id: number): string {
  if (id >= 0x01 && id <= 0x0a) return `Oxygen sensor bank ${Math.ceil(id / 2)} sensor ${((id - 1) % 2) + 1}`;
  const named: Record<number, string> = {
    0x21: 'Catalyst bank 1',
    0x22: 'Catalyst bank 2',
    0x31: 'EGR system bank 1',
    0x32: 'EGR system bank 2',
    0x39: 'Oxygen sensor heater bank 1',
    0x3a: 'Oxygen sensor heater bank 2',
    0x41: 'Evaporative system, 0.090 inch leak',
    0x42: 'Evaporative system, 0.040 inch leak',
    0x43: 'Evaporative system, 0.020 inch leak',
    0x44: 'Evaporative purge flow',
    0xa1: 'Misfire general data',
    0xa2: 'Misfire cylinder 1',
    0xa3: 'Misfire cylinder 2',
    0xa4: 'Misfire cylinder 3',
    0xa5: 'Misfire cylinder 4',
    0xa6: 'Misfire cylinder 5',
    0xa7: 'Misfire cylinder 6',
    0xa8: 'Misfire cylinder 7',
    0xa9: 'Misfire cylinder 8',
  };
  return named[id] ?? `Monitor 0x${id.toString(16).toUpperCase().padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Mode 09 — vehicle information beyond the VIN
// ---------------------------------------------------------------------------
//
// Calibration IDs and their verification numbers are how you tell whether an
// ECU is running factory software. On a modified or collector vehicle that is
// often the most interesting fact on the bus.

export interface VehicleInfo {
  calibrationIds: string[];
  calibrationVerificationNumbers: string[];
  ecuName: string | null;
}

/**
 * Mode 09 payload, across both framings.
 *
 * On CAN the byte after the PID is the number of items that follow. On K-line
 * the same position is a message sequence number, and the payload is split
 * across as many replies as it takes, four bytes at a time. Reading only the
 * first reply there truncates a 16-character calibration id to four characters,
 * which is exactly what a 2005 Lexus produced.
 */
function mode09Payload(raw: string, pid: number): { data: number[]; count: number | null } | null {
  const echo = '49' + pid.toString(16).toUpperCase().padStart(2, '0');
  const matching: number[][] = [];
  for (const response of splitResponses(raw)) {
    const at = response.indexOf(echo);
    if (at < 0) continue;
    const bytes = hexToBytes(response.slice(at + 4));
    if (bytes.length > 1) matching.push(bytes);
  }

  if (matching.length === 0) return null;

  // A single reply carries a count byte; several replies carry sequence bytes.
  if (matching.length === 1) return { data: matching[0].slice(1), count: matching[0][0] || null };
  return { data: matching.flatMap((b) => b.slice(1)), count: null };
}

const printable = (bytes: number[]) =>
  bytes
    .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : ''))
    .join('')
    .trim();

export function parseCalibrationIds(raw: string): string[] {
  const payload = mode09Payload(raw, 0x04);
  if (!payload) return [];
  // Calibration ids are 16 bytes each, padded with nulls.
  const count = payload.count ?? Math.max(1, Math.ceil(payload.data.length / 16));
  const ids: string[] = [];
  for (let i = 0; i < count; i++) {
    const text = printable(payload.data.slice(i * 16, (i + 1) * 16));
    if (text) ids.push(text);
  }
  return ids;
}

export function parseCvns(raw: string): string[] {
  const payload = mode09Payload(raw, 0x06);
  if (!payload) return [];
  const count = payload.count ?? Math.max(1, Math.floor(payload.data.length / 4));
  const cvns: string[] = [];
  for (let i = 0; i < count; i++) {
    const chunk = payload.data.slice(i * 4, (i + 1) * 4);
    if (chunk.length < 4) break;
    cvns.push(chunk.map((b) => b.toString(16).toUpperCase().padStart(2, '0')).join(''));
  }
  return cvns;
}

export function parseEcuName(raw: string): string | null {
  const payload = mode09Payload(raw, 0x0a);
  return payload ? printable(payload.data) || null : null;
}
